/**
 * Button — V1.1 Studio Slate primary uses slate ink as the committing color.
 *
 * Brand spec §13.1: no gradients. The primary button is `bg-ink`, hovering to
 * `bg-ink-2`. Sky is reserved for accent rails and links.
 */
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn.js";

const buttonStyles = cva(
  "inline-flex items-center justify-center gap-2 font-medium transition-colors focus-visible:outline-2 focus-visible:outline-accent focus-visible:outline-offset-2 disabled:opacity-50 disabled:pointer-events-none rounded-sm",
  {
    variants: {
      intent: {
        primary: "bg-ink text-paper hover:bg-ink-2",
        // Bordered/outline action. `ghost` has no border at all, so a
        // secondary action rendered with it reads as plain text rather than a
        // control — this is the variant to reach for whenever a button needs
        // to look clickable without competing with the page's primary.
        secondary: "bg-paper text-ink-2 border border-line hover:bg-bg hover:border-line-2",
        ghost: "bg-transparent text-ink hover:bg-raised",
        link: "bg-transparent text-accent-hover hover:underline",
        danger: "bg-bad text-paper hover:opacity-90",
      },
      size: {
        sm: "h-8 px-3 text-sm",
        md: "h-9 px-4 text-sm",
        lg: "h-10 px-5 text-sm",
      },
    },
    defaultVariants: {
      intent: "primary",
      size: "md",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonStyles> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, intent, size, asChild = false, ...props },
  ref,
) {
  const Comp = asChild ? Slot : "button";
  return <Comp ref={ref} className={cn(buttonStyles({ intent, size }), className)} {...props} />;
});
